/**
 * The "here is what you are about to change" summary shared by the admin forms that
 * confirm before they write.
 *
 * A settings or pricing form is a wall of inputs where the one field that was actually
 * touched is invisible by the time the admin reaches the button -- and the values are
 * live the moment they are saved. So these screens submit into a confirmation dialog
 * that lists nothing but the differences, and repeat the same list back once the write
 * lands. Both lists come from here, so what is approved is literally what is reported.
 */

// Formatters for the values in the list. A field with no formatter falls back to
// `asText`, which is safe for anything.
export const asText = (value) =>
    value === "" || value === null || value === undefined
        ? "Not set"
        : String(value);

// Long free text (a description) would push the dialog off the screen; the summary only
// needs to be recognisable, the field itself is still on the form behind it.
export const asShortText = (limit = 60) => (value) => {
    const text = asText(value);
    return text.length > limit ? `${text.slice(0, limit).trimEnd()}…` : text;
};

export const asYen = (value) =>
    value === "" || value === null || value === undefined
        ? "Not set"
        : `¥${Number(value).toLocaleString("en-US")}`;

export const withUnit = (unit) => (value) =>
    value === "" || value === null || value === undefined
        ? "Not set"
        : `${value} ${unit}`;

export const asOnOff = (value) => (value ? "On" : "Off");

/**
 * The fields of `next` that differ from `saved`, as `{ label, from, to }`.
 *
 * `fields` is `[{ name, label, format }]` in the order the form shows them, so the
 * summary reads in the same order the admin filled it in.
 *
 * Values are compared as strings: a number typed back over the identical stored number
 * arrives as "10" against 10, and a form that reported that as a change would train the
 * admin to click through the dialog without reading it.
 */
export const diffFields = (fields, saved, next) =>
    fields.reduce((changes, field) => {
        const before = saved?.[field.name];
        const after = next?.[field.name];

        if (String(before ?? "") === String(after ?? "")) return changes;

        const format = field.format || asText;

        return changes.concat({
            label: field.label,
            from: format(before),
            to: format(after),
        });
    }, []);

/**
 * The fields of `values` worth showing on a "you are about to create this" summary:
 * everything the admin actually filled in, in form order. Blanks are dropped rather
 * than listed as "Not set" — on a create there is no "before", so an empty field is
 * simply not part of what is being made.
 */
export const summarizeFields = (fields, values) =>
    fields.reduce((rows, field) => {
        const value = values?.[field.name];

        if (value === "" || value === null || value === undefined) return rows;

        const format = field.format || asText;

        return rows.concat({ label: field.label, value: format(value) });
    }, []);

const listClass =
    "tw:!list-none tw:!m-0 tw:!p-0 tw:!border tw:!border-[#E8E8E8] tw:!rounded-md tw:!divide-y tw:!divide-[#E8E8E8] tw:!max-h-[45vh] tw:!overflow-y-auto";

/** A flat label/value summary — what a create form is about to write. */
export const ValueList = ({ rows }) => (
    <ul className={listClass}>
        {rows.map((row) => (
            <li
                key={row.label}
                className="tw:!flex tw:!items-baseline tw:!justify-between tw:!gap-4 tw:!px-4 tw:!py-3"
            >
                <span className="tw:!text-[13px] tw:!text-[#8D8D8D]">{row.label}</span>
                <span className="tw:!text-right tw:!text-[14px] tw:!font-[600] tw:!text-[#282828]">
                    {row.value}
                </span>
            </li>
        ))}
    </ul>
);

export const ChangeList = ({ changes }) => (
    <ul className={listClass}>
        {changes.map((change) => (
            <li key={change.label} className="tw:!px-4 tw:!py-3">
                <div className="tw:!text-[13px] tw:!text-[#8D8D8D] tw:!mb-1">{change.label}</div>
                <div className="tw:!flex tw:!items-center tw:!gap-2 tw:!flex-wrap tw:!text-[14px]">
                    <span className="tw:!text-[#8D8D8D] tw:!line-through">{change.from}</span>
                    <span className="tw:!text-[#8D8D8D]">&rarr;</span>
                    <span className="tw:!font-[600] tw:!text-[#282828]">{change.to}</span>
                </div>
            </li>
        ))}
    </ul>
);

export default ChangeList;
